import { apiClient } from './api';

export interface AuthResponse {
    success: boolean;
    message?: string;
    error?: string;
}

export interface TokenResponse {
    success: boolean;
    message?: string;
    error?: string;
}

export interface RevokeTokenRequest {
    type: 'access' | 'refresh';
}

export interface RevokeAllTokenRequest {
    email: string;
}

/**
 * Auth Service - handles authentication endpoints
 */
const authService = {
    /**
     * Initialize authentication on app/dashboard load
     * Validates and refreshes tokens if needed
     */
    initializeAuth: async (signal?: AbortSignal): Promise<boolean> => {
        try {
            // First, try to validate the current access token
            try {
                const validateResult = await authService.validateAccessToken();
                if (validateResult && validateResult.valid) {
                    // Access token is still valid, no need to refresh
                    return true;
                }
            } catch (error) {
                // Access token is invalid or expired, try to refresh
            }

            // Access token is invalid/expired, try to refresh using refresh token
            const result = await authService.refreshAccessToken(signal);

            if (result && result.success) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    },

    me: async (signal?: AbortSignal): Promise<any> => {
        try {
            const response = await apiClient.post('/me/', {}, { signal });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Validate access token
     * POST /me/validate-access-token
     */
    validateAccessToken: async (): Promise<{ valid: boolean; message?: string }> => {
        try {
            const response = await apiClient.post('/me/validate-access-token');
            return { valid: true, ...response.data };
        } catch (error) {
            return { valid: false };
        }
    },

    /**
     * Refresh access token using refresh token
     * POST /me/refresh
     * Note: Refresh token is in HttpOnly cookie, sent automatically by browser
     */
    refreshAccessToken: async (signal?: AbortSignal): Promise<TokenResponse> => {
        try {
            const response = await apiClient.post('/me/refresh', {}, { signal });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Revoke a specific token (access or refresh)
     * POST /me/revoke
     */
    revokeToken: async (type: 'access' | 'refresh'): Promise<AuthResponse> => {
        try {
            const response = await apiClient.post('/me/revoke', { type });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Revoke all tokens for a user by email
     * POST /me/revoke-all
     */
    revokeAllTokens: async (email: string): Promise<AuthResponse> => {
        try {
            const response = await apiClient.post('/me/revoke-all', { email });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

};

export default authService;